import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../../services/apiService';
import { Plus, Trash2, Settings, AlertTriangle, Copy, Check, ChevronDown } from 'lucide-react';

interface ApiKey {
  id: string;
  name: string;
  key?: string;
  isActive: boolean;
  usageCount: number;
  creditsUsed: number;
  lastUsedAt?: string;
  createdAt: string;
  modelSelectionMode?: 'all' | 'custom_only' | 'global_only' | 'privacy_only';
  modelSelectionPriority?: 'rank' | 'price';
  modelListId?: string;
  activeTagSlugs?: string[];
}

interface ModelList {
  id: string;
  name: string;
  description?: string;
  items?: { modelId: string }[];
}

interface Category {
  slug: string;
  name: string;
}

export default function ApiKeys() {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [modelLists, setModelLists] = useState<ModelList[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [keyCopied, setKeyCopied] = useState(false);
  const [editingKey, setEditingKey] = useState<ApiKey | null>(null);
  const [pendingConfigKeyId, setPendingConfigKeyId] = useState<string | null>(null);
  const [showKeySettingsModal, setShowKeySettingsModal] = useState(false);
  const [keyModelSelectionMode, setKeyModelSelectionMode] = useState<'all' | 'custom_only' | 'global_only' | 'privacy_only'>('all');
  const [keyModelSelectionPriority, setKeyModelSelectionPriority] = useState<'rank' | 'price'>('rank');
  const [keyModelListId, setKeyModelListId] = useState<string>('');
  const [keyActiveTagSlugs, setKeyActiveTagSlugs] = useState<string[]>([]);
  const [tagDropdownOpen, setTagDropdownOpen] = useState(false);
  const [tagSearch, setTagSearch] = useState('');
  const [tagDropdownRect, setTagDropdownRect] = useState<DOMRect | null>(null);
  const tagDropdownRef = useRef<HTMLDivElement>(null);
  const tagTriggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    loadApiKeys();
    loadModelLists();
    loadCategories();
  }, []);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (tagDropdownRef.current && !tagDropdownRef.current.contains(e.target as Node)) {
        setTagDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadModelLists = async () => {
    try {
      const res = await apiService.getModelLists();
      setModelLists((res as any).data || res);
    } catch {
      // non-fatal
    }
  };

  const loadCategories = async () => {
    try {
      const data = await apiService.listCategories();
      setCategories((data.categories || []).filter((c: Category) => c.slug !== 'privacy'));
    } catch {
      // non-fatal
    }
  };

  const handleEditKeySettings = (key: ApiKey) => {
    setEditingKey(key);
    setKeyModelSelectionMode(key.modelSelectionMode || 'all');
    setKeyModelSelectionPriority(key.modelSelectionPriority || 'rank');
    setKeyModelListId(key.modelListId || '');
    setKeyActiveTagSlugs(key.activeTagSlugs || []);
    setShowKeySettingsModal(true);
  };

  const handleSaveKeySettings = async () => {
    if (!editingKey) return;

    try {
      await apiService.updateApiKey(editingKey.id, {
        modelSelectionMode: keyModelSelectionMode,
        modelSelectionPriority: keyModelSelectionPriority,
        modelListId: keyModelListId || null,
        activeTagSlugs: keyActiveTagSlugs,
      });
      setShowKeySettingsModal(false);
      setEditingKey(null);
      await loadApiKeys();
    } catch (err: any) {
      setError(err.message || 'Failed to save API key settings');
    }
  };

  const loadApiKeys = async () => {
    try {
      const keys = await apiService.getApiKeys();
      setApiKeys(keys);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKey = async () => {
    if (!newKeyName.trim()) return;

    try {
      const result = await apiService.createApiKey(newKeyName.trim());
      setCreatedKey(result.key);
      setPendingConfigKeyId(result.id);
      setNewKeyName('');
      setShowCreateModal(false);
      await loadApiKeys();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleRevokeKey = async (keyId: string) => {
    if (!window.confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
      return;
    }

    try {
      await apiService.revokeApiKey(keyId);
      await loadApiKeys();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const maskKey = (keyId: string) => {
    // Always show the key ID with masking - full keys are never returned after creation
    return `fxk_${keyId.substring(0, 8)}••••••••••••••••••••••••••••••••••••••••••••••••••••`;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const copyCreatedKey = () => {
    if (!createdKey) return;
    navigator.clipboard.writeText(createdKey);
    setKeyCopied(true);
    setTimeout(() => setKeyCopied(false), 3000);
  };

  const dismissCreatedKey = () => {
    setCreatedKey(null);
    setKeyCopied(false);
    if (pendingConfigKeyId) {
      const key = apiKeys.find(k => k.id === pendingConfigKeyId);
      setPendingConfigKeyId(null);
      if (key) handleEditKeySettings(key);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">API Keys</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your API keys for accessing AskArc API
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create API Key
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Created Key Modal */}
      {createdKey && (
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-70 flex items-center justify-center z-[9999] !m-0">
          <div className="bg-white rounded-xl shadow-2xl w-[480px] overflow-hidden">
            {/* Warning banner */}
            <div className="bg-amber-400 px-5 py-4 flex items-start gap-3">
              <AlertTriangle className="h-6 w-6 text-amber-900 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-amber-900 text-base">Save your API key now</p>
                <p className="text-amber-800 text-sm mt-0.5">
                  This is the <strong>only time</strong> your full key will be shown. It cannot be recovered after you close this dialog.
                </p>
              </div>
            </div>

            <div className="p-6">
              <p className="text-sm text-gray-600 mb-3">Your new API key:</p>

              {/* Key display */}
              <div className="bg-gray-900 rounded-lg p-4 flex items-center justify-between gap-3 border-2 border-amber-300">
                <code className="text-green-400 text-sm font-mono break-all flex-1 leading-relaxed">
                  {createdKey}
                </code>
              </div>

              {/* Actions */}
              <div className="mt-5 flex gap-3">
                <button
                  onClick={copyCreatedKey}
                  className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-semibold text-sm transition-all ${
                    keyCopied
                      ? 'bg-green-600 text-white'
                      : 'bg-indigo-600 text-white hover:bg-indigo-700'
                  }`}
                >
                  {keyCopied ? (
                    <>
                      <Check className="h-4 w-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4" />
                      Copy API Key
                    </>
                  )}
                </button>
                <button
                  onClick={dismissCreatedKey}
                  className="px-4 py-3 bg-gray-100 text-gray-700 rounded-lg font-semibold text-sm hover:bg-gray-200 transition-all"
                >
                  I've saved it — configure key →
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] !m-0">
          <div className="bg-white rounded-lg shadow-xl w-96 p-5">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Create New API Key
              </h3>
              <input
                type="text"
                placeholder="Enter key name"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <div className="mt-4 flex justify-end space-x-3">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateKey}
                  disabled={!newKeyName.trim()}
                  className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* API Key Settings Modal */}
      {showKeySettingsModal && editingKey && (
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] !m-0 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-xl flex flex-col" style={{ height: '560px' }}>
            {/* Header */}
            <div className="px-6 py-4 border-b flex-shrink-0">
              <h3 className="text-lg font-medium text-gray-900">
                API Key Settings: {editingKey.name}
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Configure how this API key interacts with models
              </p>
            </div>

            {/* Body — 2 columns */}
            <div className="px-6 py-5 flex-1">
              <div className="grid grid-cols-2 gap-6">

                {/* Left column — selection behaviour */}
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Model Selection Mode
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      Which models this key can access
                    </p>
                    <select
                      value={keyModelSelectionMode}
                      onChange={(e) => setKeyModelSelectionMode(e.target.value as 'all' | 'custom_only' | 'global_only' | 'privacy_only')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                    >
                      <option value="all">All Models (Global + Custom)</option>
                      <option value="custom_only">My Custom Models Only</option>
                      <option value="global_only">Global Models Only</option>
                      <option value="privacy_only">Privacy-Compliant Only</option>
                    </select>
                    <p className="mt-2 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-3 py-2">
                      {keyModelSelectionMode === 'all' && 'Uses both global and your custom endpoint models.'}
                      {keyModelSelectionMode === 'custom_only' && 'ONLY your custom endpoint models.'}
                      {keyModelSelectionMode === 'global_only' && 'ONLY global models (excludes custom).'}
                      {keyModelSelectionMode === 'privacy_only' && 'ONLY privacy-compliant models. Fails if none configured.'}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Model Selection Priority
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      Prioritise quality or cost
                    </p>
                    <select
                      value={keyModelSelectionPriority}
                      onChange={(e) => setKeyModelSelectionPriority(e.target.value as 'rank' | 'price')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                    >
                      <option value="rank">Rank First (Quality)</option>
                      <option value="price">Price First (Cost)</option>
                    </select>
                    <p className="mt-2 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-3 py-2">
                      {keyModelSelectionPriority === 'rank' && 'Best quality models preferred.'}
                      {keyModelSelectionPriority === 'price' && 'Most cost-effective models preferred.'}
                    </p>
                  </div>
                </div>

                {/* Right column — restrictions */}
                <div className="space-y-5">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-gray-700">
                        Model List <span className="text-gray-400 font-normal">(optional)</span>
                      </label>
                      <Link to="/models" className="text-xs text-indigo-600 hover:underline">Manage →</Link>
                    </div>
                    <p className="text-xs text-gray-500 mb-2">
                      Restrict to a curated list of models
                    </p>
                    <select
                      value={keyModelListId}
                      onChange={(e) => setKeyModelListId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                    >
                      <option value="">No list — all available models</option>
                      {modelLists.map(list => (
                        <option key={list.id} value={list.id}>
                          {list.name}{list.items?.length !== undefined ? ` (${list.items.length} model${list.items.length !== 1 ? 's' : ''})` : ''}
                        </option>
                      ))}
                    </select>
                    {keyModelListId && (
                      <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                        Only models in this list will be used.
                      </p>
                    )}
                  </div>

                  {categories.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tag-Based Filtering
                      </label>
                      <p className="text-xs text-gray-500 mb-2">
                        Restrict model pool when a tag fires on this key
                      </p>
                      <div className="relative" ref={tagDropdownRef}>
                        <button
                          ref={tagTriggerRef}
                          type="button"
                          onClick={() => {
                            if (tagTriggerRef.current) setTagDropdownRect(tagTriggerRef.current.getBoundingClientRect());
                            setTagDropdownOpen(o => !o);
                          }}
                          className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md text-sm bg-white hover:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                          <span className={keyActiveTagSlugs.length === 0 ? 'text-gray-400' : 'text-gray-800'}>
                            {keyActiveTagSlugs.length === 0
                              ? 'No tags selected'
                              : `${keyActiveTagSlugs.length} tag${keyActiveTagSlugs.length !== 1 ? 's' : ''} selected`}
                          </span>
                          <ChevronDown size={14} className={`text-gray-400 transition-transform ${tagDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>

                        {tagDropdownOpen && tagDropdownRect && (
                          <div
                            ref={tagDropdownRef}
                            className="fixed z-[10000] bg-white border border-gray-200 rounded-md shadow-lg"
                            style={{
                              top: tagDropdownRect.bottom + 4,
                              left: tagDropdownRect.left,
                              width: tagDropdownRect.width,
                            }}
                          >
                            <div className="px-2 py-2 border-b border-gray-100">
                              <input
                                type="text"
                                value={tagSearch}
                                onChange={e => setTagSearch(e.target.value)}
                                placeholder="Search tags…"
                                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                autoFocus
                                onClick={e => e.stopPropagation()}
                              />
                            </div>
                            <div className="max-h-40 overflow-y-auto">
                              {categories.filter(cat => cat.name.toLowerCase().includes(tagSearch.toLowerCase())).map(cat => {
                                const checked = keyActiveTagSlugs.includes(cat.slug);
                                return (
                                  <label key={cat.slug} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setKeyActiveTagSlugs(prev => [...prev, cat.slug]);
                                        } else {
                                          setKeyActiveTagSlugs(prev => prev.filter(s => s !== cat.slug));
                                        }
                                      }}
                                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                    />
                                    <span className="text-sm text-gray-700">{cat.name}</span>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                      <p className={`mt-2 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-3 py-2 ${keyActiveTagSlugs.length === 0 ? 'invisible' : ''}`}>
                        When these tags fire, only models with the matching tag enabled will be used.
                      </p>
                    </div>
                  )}
                </div>

              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t flex-shrink-0 flex justify-end space-x-3">
              <button
                onClick={() => { setShowKeySettingsModal(false); setEditingKey(null); setTagDropdownOpen(false); setTagSearch(''); }}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveKeySettings}
                className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 text-sm"
              >
                Save Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* API Keys List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {apiKeys.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No API keys yet. Create your first one to get started.</p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {apiKeys.map((key) => (
              <li key={key.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900">{key.name}</h3>

                    <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                      <span>Usage: {key.usageCount} requests</span>
                      <span>Cost: ${key.creditsUsed.toFixed(4)}</span>
                      <span>
                        Last used: {key.lastUsedAt
                          ? new Date(key.lastUsedAt).toLocaleDateString()
                          : 'Never'
                        }
                      </span>
                      <span>
                        Created: {new Date(key.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  <div className="ml-4 flex items-center space-x-3">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      key.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {key.isActive ? 'Active' : 'Revoked'}
                    </span>
                    {key.isActive && (
                      <>
                        <button
                          onClick={() => handleEditKeySettings(key)}
                          className="text-gray-600 hover:text-gray-800"
                          title="Configure settings for this API key"
                        >
                          <Settings size={20} />
                        </button>
                        <button
                          onClick={() => handleRevokeKey(key.id)}
                          className="text-red-600 hover:text-red-800"
                          title="Revoke this API key"
                        >
                          <Trash2 size={20} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
